import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, Save, Search, UserPlus, Users, Pencil, Trash2, X, Wallet, Fuel } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EXPENSE_COLS = [
  { key: 'fuel_expense', label: 'Fuel' },
  { key: 'salary_expense', label: 'Salary' },
  { key: 'installment_expense', label: 'Installment' },
  { key: 'breakdown_expense', label: 'Breakdown' },
  { key: 'assistant_expense', label: 'Assistant' },
  { key: 'other_expense', label: 'Other' },
];

const computeRow = (r) => {
  const exp = EXPENSE_COLS.reduce((s, c) => s + (parseFloat(r[c.key]) || 0), 0);
  const rev = parseFloat(r.revenue) || 0;
  return { ...r, total_expenses: Math.round(exp * 100) / 100, net_profit: Math.round((rev - exp) * 100) / 100 };
};

export default function DriverPnLPage() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const today = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(today);
  const [rows, setRows] = useState([]);
  const [originals, setOriginals] = useState({});  // driver_id → original snapshot
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [search, setSearch] = useState('');

  // Curated drivers + known plates for autocomplete
  const [people, setPeople] = useState([]);
  const [plateOptions, setPlateOptions] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null); // null = none, {} = new, {...} = edit
  const [personForm, setPersonForm] = useState({ driver_name: '', registration_number: '', owner_name: '', linked_staff_id: '', linked_assistant_staff_id: '' });
  const [personSaving, setPersonSaving] = useState(false);
  const [pullingSalaries, setPullingSalaries] = useState(false);
  const [pullingFuel, setPullingFuel] = useState(false);
  const [staffList, setStaffList] = useState([]);

  const fetchPeople = useCallback(async () => {
    try {
      const [pplRes, vehRes, staffRes] = await Promise.all([
        axios.get(`${API_URL}/driver-pnl/people`, { headers }),
        axios.get(`${API_URL}/fuel-tracker/vehicles`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/internal-staff`, { headers }).catch(() => ({ data: [] })),
      ]);
      setPeople(pplRes.data || []);
      setPlateOptions((vehRes.data || []).map(v => v.registration).filter(Boolean));
      setStaffList((staffRes.data || []).filter(s => s.is_active !== false));
    } catch { /* silent */ }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/driver-pnl`, { headers, params: { month } });
      const fresh = (res.data?.rows || []).map(computeRow);
      setRows(fresh);
      const orig = {};
      for (const r of fresh) orig[r.driver_id] = { ...r };
      setOriginals(orig);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchPeople(); }, [fetchPeople]);

  const updateCell = (driver_id, key, value) => {
    setRows(prev => prev.map(r => r.driver_id === driver_id ? computeRow({ ...r, [key]: value }) : r));
  };

  const isDirty = (r) => {
    const o = originals[r.driver_id];
    if (!o) return true;
    if ((r.notes || '') !== (o.notes || '')) return true;
    if ((parseFloat(r.revenue) || 0) !== (parseFloat(o.revenue) || 0)) return true;
    return EXPENSE_COLS.some(c => (parseFloat(r[c.key]) || 0) !== (parseFloat(o[c.key]) || 0));
  };

  const saveRow = async (r) => {
    setSavingId(r.driver_id);
    try {
      const payload = {
        driver_id: r.driver_id,
        month,
        revenue: parseFloat(r.revenue) || 0,
        notes: r.notes || '',
        ...Object.fromEntries(EXPENSE_COLS.map(c => [c.key, parseFloat(r[c.key]) || 0])),
      };
      const res = await axios.put(`${API_URL}/driver-pnl`, payload, { headers });
      const saved = computeRow(res.data);
      setRows(prev => prev.map(x => x.driver_id === r.driver_id ? saved : x));
      setOriginals(prev => ({ ...prev, [r.driver_id]: { ...saved } }));
      toast.success(`Saved ${r.driver_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId('');
    }
  };

  const saveAll = async () => {
    const dirty = rows.filter(isDirty);
    if (!dirty.length) { toast.info('Nothing to save'); return; }
    setSavingId('all');
    let ok = 0;
    for (const r of dirty) {
      try {
        const payload = {
          driver_id: r.driver_id,
          month,
          revenue: parseFloat(r.revenue) || 0,
          notes: r.notes || '',
          ...Object.fromEntries(EXPENSE_COLS.map(c => [c.key, parseFloat(r[c.key]) || 0])),
        };
        await axios.put(`${API_URL}/driver-pnl`, payload, { headers });
        ok += 1;
      } catch { /* per-row failure swallowed */ }
    }
    toast.success(`Saved ${ok} of ${dirty.length} rows`);
    setSavingId('');
    fetchData();
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(r => (r.driver_name || '').toLowerCase().includes(q) || (r.owner_name || '').toLowerCase().includes(q) || (r.registration_number || '').toLowerCase().includes(q));
  }, [rows, search]);

  const totals = useMemo(() => {
    const t = { revenue: 0, total_expenses: 0, net_profit: 0 };
    EXPENSE_COLS.forEach(c => { t[c.key] = 0; });
    for (const r of filtered) {
      // parseFloat protects against the string concatenation bug when a user
      // is mid-edit (the <Input> stores the typed value as a string).
      t.revenue += parseFloat(r.revenue) || 0;
      t.total_expenses += parseFloat(r.total_expenses) || 0;
      t.net_profit += parseFloat(r.net_profit) || 0;
      EXPENSE_COLS.forEach(c => { t[c.key] += parseFloat(r[c.key]) || 0; });
    }
    return t;
  }, [filtered]);

  const dirtyCount = rows.filter(isDirty).length;

  // ---- Curated driver CRUD ----
  const openAddPerson = () => {
    setEditingPerson({});
    setPersonForm({ driver_name: '', registration_number: '', owner_name: '', linked_staff_id: '', linked_assistant_staff_id: '' });
  };
  const openEditPerson = (p) => {
    setEditingPerson(p);
    setPersonForm({
      driver_name: p.driver_name || '',
      registration_number: p.registration_number || '',
      owner_name: p.owner_name || '',
      linked_staff_id: p.linked_staff_id || '',
      linked_assistant_staff_id: p.linked_assistant_staff_id || '',
    });
  };
  const closePersonForm = () => { setEditingPerson(null); };

  const savePerson = async () => {
    if (!personForm.driver_name.trim()) { toast.error('Driver name required'); return; }
    setPersonSaving(true);
    try {
      const payload = {
        driver_name: personForm.driver_name.trim(),
        registration_number: personForm.registration_number.trim().toUpperCase(),
        owner_name: personForm.owner_name.trim(),
        linked_staff_id: personForm.linked_staff_id || null,
        linked_assistant_staff_id: personForm.linked_assistant_staff_id || null,
      };
      if (editingPerson?.id) {
        await axios.put(`${API_URL}/driver-pnl/people/${editingPerson.id}`, payload, { headers });
        toast.success('Driver updated');
      } else {
        await axios.post(`${API_URL}/driver-pnl/people`, payload, { headers });
        toast.success('Driver added');
      }
      closePersonForm();
      await fetchPeople();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setPersonSaving(false);
    }
  };

  const deletePerson = async (p) => {
    if (!window.confirm(`Remove "${p.driver_name}" from the P&L list? Any saved monthly P&L rows for this person will also be deleted.`)) return;
    try {
      await axios.delete(`${API_URL}/driver-pnl/people/${p.id}`, { headers });
      toast.success('Removed');
      await fetchPeople();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const pullSalaries = async (overwrite = false) => {
    const linked = people.filter(p => p.linked_staff_id || p.linked_assistant_staff_id).length;
    if (linked === 0) {
      toast.error('No P&L driver is linked to an Internal Staff record yet. Open Manage Drivers and pick a staff member first.');
      return;
    }
    const message = overwrite
      ? `Overwrite salary + assistant for ${month} on all ${linked} linked drivers with values from Internal Staff payslips?`
      : `Pull salaries for ${month} from Internal Staff payslips for ${linked} linked drivers? Rows that already have non-zero salary/assistant will be kept.`;
    if (!window.confirm(message)) return;
    setPullingSalaries(true);
    try {
      const res = await axios.post(`${API_URL}/driver-pnl/pull-salaries`, null, {
        headers,
        params: { month, overwrite },
      });
      const d = res.data || {};
      toast.success(
        `Pulled from payslips · Updated ${d.updated} · ${d.skipped_no_link} unlinked · ${d.skipped_no_payslip} no payslip · ${d.skipped_existing} kept existing`,
        { duration: 8000 },
      );
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pull failed');
    } finally {
      setPullingSalaries(false);
    }
  };

  const pullFuel = async (overwrite = false) => {
    const withPlate = people.filter(p => (p.registration_number || '').trim()).length;
    if (withPlate === 0) {
      toast.error('No P&L driver has a Number Plate set. Open Manage Drivers → Edit → enter the plate first.');
      return;
    }
    const message = overwrite
      ? `Overwrite fuel_expense for ${month} on all ${withPlate} drivers with plates? This replaces any values you've already captured.`
      : `Pull fuel totals from the Fuel Tracker for ${month} into ${withPlate} drivers with plates? Rows that already have a non-zero fuel value will be kept.`;
    if (!window.confirm(message)) return;
    setPullingFuel(true);
    try {
      const res = await axios.post(`${API_URL}/driver-pnl/pull-fuel`, null, {
        headers,
        params: { month, overwrite },
      });
      const d = res.data || {};
      toast.success(
        `Pulled fuel · Updated ${d.updated} · ${d.skipped_no_plate} no plate · ${d.skipped_no_fuel} no entries · ${d.skipped_existing} kept existing`,
        { duration: 8000 },
      );
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pull failed');
    } finally {
      setPullingFuel(false);
    }
  };

  const cellInput = (r, key) => (
    <Input
      type="number"
      min="0"
      step="0.01"
      value={r[key] ?? 0}
      onChange={(e) => updateCell(r.driver_id, key, e.target.value)}
      className="h-7 text-xs text-right font-mono"
      data-testid={`pnl-${key}-${r.driver_id}`}
    />
  );

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-emerald-600" /> Per-Driver P&amp;L
            </h1>
            <p className="text-sm text-muted-foreground">
              Capture revenue + expenses per driver per month. Net Profit auto-calculates. Every figure is editable.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setManageOpen(true)}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="manage-drivers-btn"
            >
              <Users className="h-4 w-4 mr-1" /> Manage Drivers ({people.length})
            </Button>
            <Button
              variant="outline"
              onClick={() => pullSalaries(false)}
              disabled={pullingSalaries}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="pull-salaries-btn"
              title="Pull Driver + Assistant salaries for this month from each linked Internal Staff member's payslip (gross_pay). Rows with existing values are kept."
            >
              <Wallet className="h-4 w-4 mr-1" />
              {pullingSalaries ? 'Pulling…' : 'Pull From Payslips'}
            </Button>
            <Button
              variant="outline"
              onClick={() => pullFuel(false)}
              disabled={pullingFuel}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="pull-fuel-btn"
              title="Sum every Fuel Tracker entry for each driver's Number Plate this month → into Fuel column. Rows with existing fuel are kept."
            >
              <Fuel className="h-4 w-4 mr-1" />
              {pullingFuel ? 'Pulling…' : 'Pull Fuel'}
            </Button>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-3 text-muted-foreground" />
              <Input
                placeholder="Search driver / owner / reg"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 w-60"
                data-testid="pnl-search"
              />
            </div>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-44"
              data-testid="pnl-month"
            />
            <Button
              onClick={saveAll}
              disabled={savingId === 'all' || dirtyCount === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-all-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              {savingId === 'all' ? 'Saving…' : dirtyCount ? `Save All (${dirtyCount})` : 'Save All'}
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Total Revenue</div>
              <div className="text-2xl font-bold text-emerald-900 mt-1">{R(totals.revenue)}</div>
              <div className="text-xs text-emerald-700 mt-1">{filtered.length} driver{filtered.length === 1 ? '' : 's'} shown</div>
            </CardContent>
          </Card>
          <Card className="bg-rose-50 border-rose-200">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-rose-700 font-semibold">Total Expenses</div>
              <div className="text-2xl font-bold text-rose-900 mt-1">{R(totals.total_expenses)}</div>
              <div className="text-xs text-rose-700 mt-1">Fuel + Salary + Installment + Breakdown + Assistant + Other</div>
            </CardContent>
          </Card>
          <Card className={`${totals.net_profit >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
            <CardContent className="p-4">
              <div className={`text-[11px] uppercase tracking-wide font-semibold ${totals.net_profit >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>Total Net Profit</div>
              <div className={`text-2xl font-bold mt-1 ${totals.net_profit >= 0 ? 'text-blue-900' : 'text-amber-900'}`}>{R(totals.net_profit)}</div>
              <div className="text-xs text-muted-foreground mt-1">{month}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-700 font-semibold">Margin</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {totals.revenue > 0 ? `${(totals.net_profit / totals.revenue * 100).toFixed(1)}%` : '—'}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Net ÷ Revenue</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Driver P&amp;L · {month}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            {loading ? (
              <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">No matching drivers.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white z-10" style={{ minWidth: 220 }}>Driver / Vehicle</TableHead>
                    <TableHead className="text-right" style={{ minWidth: 130 }}>Revenue</TableHead>
                    {EXPENSE_COLS.map(c => (
                      <TableHead key={c.key} className="text-right" style={{ minWidth: 110 }}>{c.label}</TableHead>
                    ))}
                    <TableHead className="text-right" style={{ minWidth: 120 }}>Total Exp</TableHead>
                    <TableHead className="text-right" style={{ minWidth: 130 }}>Net Profit</TableHead>
                    <TableHead style={{ minWidth: 70 }}></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="pnl-tbody">
                  {filtered.map((r) => {
                    const dirty = isDirty(r);
                    return (
                      <TableRow key={r.driver_id} className={dirty ? 'bg-amber-50' : ''} data-testid={`pnl-row-${r.driver_id}`}>
                        <TableCell className="sticky left-0 bg-inherit z-10">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="font-medium text-sm">{r.driver_name}</div>
                              <div className="text-[10px] text-muted-foreground">
                                {r.owner_name && <span>{r.owner_name}</span>}
                                {r.registration_number && <span> · {r.registration_number}</span>}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-rose-600 hover:bg-rose-50 shrink-0"
                              onClick={() => deletePerson(people.find(p => p.id === r.driver_id) || { id: r.driver_id, driver_name: r.driver_name })}
                              data-testid={`pnl-row-delete-${r.driver_id}`}
                              title="Remove this driver from the P&L list"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{cellInput(r, 'revenue')}</TableCell>
                        {EXPENSE_COLS.map(c => (
                          <TableCell key={c.key} className="text-right">{cellInput(r, c.key)}</TableCell>
                        ))}
                        <TableCell className="text-right font-mono text-xs text-rose-700">{R(r.total_expenses)}</TableCell>
                        <TableCell className={`text-right font-mono font-bold ${r.net_profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{R(r.net_profit)}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={dirty ? 'default' : 'ghost'}
                            disabled={!dirty || savingId === r.driver_id}
                            onClick={() => saveRow(r)}
                            className={`h-7 px-2 ${dirty ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-muted-foreground'}`}
                            data-testid={`save-pnl-${r.driver_id}`}
                          >
                            {savingId === r.driver_id ? '…' : (dirty ? 'Save' : '✓')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-slate-100 font-bold">
                    <TableCell className="sticky left-0 bg-slate-100 z-10">TOTAL</TableCell>
                    <TableCell className="text-right font-mono text-emerald-800">{R(totals.revenue)}</TableCell>
                    {EXPENSE_COLS.map(c => (
                      <TableCell key={`t-${c.key}`} className="text-right font-mono text-xs text-rose-700">{R(totals[c.key])}</TableCell>
                    ))}
                    <TableCell className="text-right font-mono text-rose-800">{R(totals.total_expenses)}</TableCell>
                    <TableCell className={`text-right font-mono ${totals.net_profit >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>{R(totals.net_profit)}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={EXPENSE_COLS.length + 5} className="sticky left-0 bg-white">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={openAddPerson}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="add-driver-inline-btn"
                      >
                        <UserPlus className="h-3 w-3 mr-1" /> Add a Driver to the P&amp;L
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          Tip: Amber-shaded rows have unsaved changes. Use <b>Save All</b> at the top to commit every dirty row in one go.
        </p>

        {/* Known plates datalist — shared by add/edit driver dialog */}
        <datalist id="pnl-plate-options">
          {plateOptions.map(p => <option key={p} value={p}>{p}</option>)}
        </datalist>

        {/* ====== MANAGE CURATED DRIVERS DIALOG ====== */}
        <Dialog open={manageOpen} onOpenChange={setManageOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" /> Manage P&amp;L Drivers
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Curated list that appears on the P&amp;L grid. Add a driver, link a number plate, then save the monthly figures on the page behind this dialog.
                </p>
                <Button size="sm" onClick={openAddPerson} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-pnl-person-btn">
                  <UserPlus className="h-3 w-3 mr-1" /> Add Driver
                </Button>
              </div>
              <div className="border rounded-md max-h-[55vh] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50">
                    <TableRow>
                      <TableHead>Driver Name</TableHead>
                      <TableHead>Number Plate</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Driver → Staff</TableHead>
                      <TableHead>Assistant → Staff</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="pnl-people-tbody">
                    {people.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No drivers yet. Click <b>Add Driver</b>.</TableCell></TableRow>
                    ) : people.map(p => (
                      <TableRow key={p.id} data-testid={`pnl-person-row-${p.id}`}>
                        <TableCell className="font-medium text-sm">{p.driver_name}</TableCell>
                        <TableCell className="font-mono text-xs text-amber-700">{p.registration_number || <span className="text-muted-foreground italic">— not linked —</span>}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{p.owner_name || '—'}</TableCell>
                        <TableCell className="text-xs text-indigo-700">{p.linked_staff_name || <span className="text-muted-foreground italic">not linked</span>}</TableCell>
                        <TableCell className="text-xs text-emerald-700">{p.linked_assistant_staff_name || <span className="text-muted-foreground italic">not linked</span>}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditPerson(p)} data-testid={`edit-pnl-person-${p.id}`}><Pencil className="h-3 w-3" /></Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" onClick={() => deletePerson(p)} data-testid={`delete-pnl-person-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ====== ADD / EDIT PERSON FORM ====== */}
        <Dialog open={editingPerson !== null} onOpenChange={(o) => !o && closePersonForm()}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingPerson?.id ? <Pencil className="h-5 w-5 text-blue-600" /> : <UserPlus className="h-5 w-5 text-emerald-600" />}
                {editingPerson?.id ? 'Edit P&L Driver' : 'Add P&L Driver'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={personForm.driver_name}
                  onChange={e => setPersonForm({ ...personForm, driver_name: e.target.value.toUpperCase() })}
                  placeholder="e.g. JOHN DOE"
                  className="uppercase"
                  data-testid="pnl-person-name"
                />
              </div>
              <div>
                <Label>Linked Number Plate <span className="text-xs text-muted-foreground">(optional — pick from known plates or type a new one)</span></Label>
                <Input
                  list="pnl-plate-options"
                  value={personForm.registration_number}
                  onChange={e => setPersonForm({ ...personForm, registration_number: e.target.value.toUpperCase() })}
                  placeholder="e.g. CA 123-456"
                  className="uppercase font-mono"
                  data-testid="pnl-person-plate"
                />
              </div>
              <div>
                <Label>Owner <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Input
                  value={personForm.owner_name}
                  onChange={e => setPersonForm({ ...personForm, owner_name: e.target.value })}
                  placeholder="Vehicle owner"
                  data-testid="pnl-person-owner"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 pt-1 border-t">
                <div>
                  <Label className="flex items-center gap-1">
                    <Wallet className="h-3.5 w-3.5 text-indigo-600" /> Driver — Internal Staff Link
                  </Label>
                  <Select
                    value={personForm.linked_staff_id || 'none'}
                    onValueChange={(v) => setPersonForm({ ...personForm, linked_staff_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="pnl-person-staff-link"><SelectValue placeholder="Pick a staff member" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— not linked —</SelectItem>
                      {staffList.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.full_name}{s.position ? ` (${s.position})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">Pulls this staff member's <b>payslip gross pay</b> into <b>Salary</b> at month-end.</p>
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Wallet className="h-3.5 w-3.5 text-emerald-600" /> Assistant — Internal Staff Link
                  </Label>
                  <Select
                    value={personForm.linked_assistant_staff_id || 'none'}
                    onValueChange={(v) => setPersonForm({ ...personForm, linked_assistant_staff_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="pnl-person-assistant-link"><SelectValue placeholder="Pick a staff member" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— not linked —</SelectItem>
                      {staffList.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.full_name}{s.position ? ` (${s.position})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">Pulls this staff member's <b>payslip gross pay</b> into <b>Assistant</b> at month-end.</p>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={closePersonForm}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                <Button
                  onClick={savePerson}
                  disabled={personSaving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-pnl-person-btn"
                >
                  <Save className="h-4 w-4 mr-1" /> {personSaving ? 'Saving…' : (editingPerson?.id ? 'Update' : 'Add Driver')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
